/**
 * CodableSettingsHub — jednotné **modulové** nastavení Codable (ne per-projekt).
 *
 * Sekce:
 *  1) Veřejné body + oprávnění projektů
 *  2) Připojení IDE (MCP)
 *  3) Vlastníci / LLM allow-list
 *
 * Per-projekt (varianta, backend, git…) zůstává ve workspace → ProjectSettingsModal.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plug } from 'lucide-react';
import McpConnectModal from '../../../components/McpConnectModal';
import { api } from '../../../lib/api';
import { Badge, Button, Card, CardContent, SectionHeader } from '../../../components/ui';
import type { WidgetRenderProps } from '../../../widgets/types';
import { EndpointsAccessPanel } from './EndpointsAccessPanel';
import { ProjectMembersPanel } from './ProjectMembersPanel';
import { LlmAllowlistPanel } from './LlmAllowlistPanel';

export function CodableSettingsHub(_props: WidgetRenderProps = {} as WidgetRenderProps) {
  const { t } = useTranslation();
  const [connectOpen, setConnectOpen] = useState(false);

  const { data: ideConnData } = useQuery({
    queryKey: ['acm-connect-tokens', 'codable'],
    queryFn: () =>
      api.mcpSession<{ ok: boolean; tokens?: { label: string; enabled: boolean }[] }>(
        'acm-self.connect',
        { action: 'list', module: 'codable' },
      ),
    staleTime: 60_000,
    retry: false,
  });
  const tokens = (ideConnData?.tokens ?? []).filter((tk) => tk.enabled);

  return (
    <div className='space-y-8 p-1'>
      <div>
        <h2 className='text-base font-semibold text-white'>
          {t('codableSettings.title', 'Codable — settings')}
        </h2>
        <p className='text-xs text-gray-500 mt-1 max-w-2xl'>
          {t(
            'codableSettings.hubIntro',
            'Module-wide access and IDE. Project variant, domains and backend are in the project workspace (gear).',
          )}
        </p>
      </div>

      {/* 1 — Public endpoints + project permissions (own cards/i18n) */}
      <section>
        <EndpointsAccessPanel />
      </section>

      {/* 2 — IDE / MCP connect */}
      <section className='space-y-2'>
        <SectionHeader
          title={t('codableSettings.sectionIde', 'IDE connection')}
          description={t(
            'codableSettings.sectionIdeDesc',
            'MCP tokens for VS Code, VSCodium, Antigravity and other IDEs (this session).',
          )}
        />
        <Card>
          <CardContent className='flex flex-wrap items-center justify-between gap-3 py-4'>
            <div className='flex items-center gap-3 min-w-0'>
              <div className='w-10 h-10 rounded-xl bg-amber-500/10 text-amber-400 flex items-center justify-center shrink-0'>
                <Plug size={18} />
              </div>
              <div className='min-w-0'>
                <div className='text-sm text-white font-medium'>
                  {tokens.length > 0
                    ? t('codable.connectedCount', 'Connected: {{n}}', { n: tokens.length })
                    : t('codable.connectIde', 'Connect IDE')}
                </div>
                <div className='text-[11px] text-gray-500 truncate'>
                  {tokens.length > 0
                    ? tokens.map((tk) => tk.label).join(', ')
                    : t(
                      'codableSettings.ideNone',
                      'No active MCP token — generate one for IDE access.',
                    )}
                </div>
              </div>
            </div>
            <div className='flex items-center gap-2'>
              {tokens.length > 0 && (
                <Badge variant='success'>{tokens.length}</Badge>
              )}
              <Button
                variant={tokens.length > 0 ? 'secondary' : 'primary'}
                icon={<Plug size={15} />}
                onClick={() => setConnectOpen(true)}
              >
                {tokens.length > 0
                  ? t('codableSettings.ideManage', 'Manage')
                  : t('codable.connectIde', 'Connect IDE')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 3 — Owners */}
      <section className='space-y-2'>
        <SectionHeader
          title={t('codableSettings.tabMembers', 'Project owners')}
          description={t(
            'codableSettings.sectionMembersDesc',
            'Who owns which Codable project (entity groups).',
          )}
        />
        <Card>
          <CardContent className='pt-4'>
            <ProjectMembersPanel />
          </CardContent>
        </Card>
      </section>

      {/* 4 — LLM */}
      <section className='space-y-2'>
        <SectionHeader
          title={t('codableSettings.tabLlm', 'LLM access')}
          description={t(
            'codableSettings.sectionLlmDesc',
            'Tenant / project allow-list and BYOK for Codable chat.',
          )}
        />
        <Card>
          <CardContent className='pt-4'>
            <LlmAllowlistPanel />
          </CardContent>
        </Card>
      </section>

      {connectOpen && (
        <McpConnectModal module='codable' onClose={() => setConnectOpen(false)} />
      )}
    </div>
  );
}

export default CodableSettingsHub;
